import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.space';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'VigorBuy Spreadsheet Security Guide: How to Protect Your Business Data',
  description: 'A complete security framework for spreadsheet users — covering access control, encryption, backup strategies, safe sharing, and regulatory compliance to ensure your sensitive business data stays protected.',
  author: {
    '@type': 'Organization',
    name: 'VigorBuy Spread Team',
  },
  publisher: {
    '@type': 'Organization',
    name: 'VigorBuy Spread',
    url: siteUrl,
  },
  datePublished: '2026-07-22',
  dateModified: '2026-07-22',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/vigorbuy-spreadsheet-security`,
  },
  inLanguage: 'en',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Security Guide',
      item: `${siteUrl}/vigorbuy-spreadsheet-security`,
    },
  ],
};

const securityPrinciples = [
  {
    icon: 'ri-lock-password-line',
    title: 'Access Control & Authentication',
    desc: 'Implement role-based permissions that restrict spreadsheet access to authorized personnel only. Use strong, unique passwords for each workbook and enable two-factor authentication wherever supported. Never share master credentials through unencrypted channels — use password managers for team credential distribution.',
  },
  {
    icon: 'ri-shield-keyhole-line',
    title: 'Data Encryption Standards',
    desc: 'Apply encryption both at rest and in transit. Ensure all spreadsheet files stored on cloud platforms use AES-256 encryption. When sharing spreadsheets externally, use password-protected links with expiration dates and disable download permissions for sensitive documents.',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Backup & Disaster Recovery',
    desc: 'Configure automated daily backups with at least 30-day retention. Maintain backups in geographically separate locations from primary storage. Test restoration procedures quarterly to verify backup integrity and ensure recovery time objectives are achievable under real conditions.',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Data Privacy & Masking',
    desc: 'Hide or mask sensitive columns when sharing spreadsheets with external stakeholders. Use cell-level protection to prevent accidental edits to formula cells. Implement data classification tags that clearly mark which sheets contain personally identifiable or financially sensitive information.',
  },
  {
    icon: 'ri-history-line',
    title: 'Audit Trail & Version Control',
    desc: 'Enable detailed version history tracking that records who made each change and when. Configure alerts for unexpected modifications to critical formula cells or data validation rules. Maintain change logs that support compliance requirements and forensic investigation needs.',
  },
  {
    icon: 'ri-share-forward-line',
    title: 'Safe Sharing Practices',
    desc: 'Use view-only sharing links with expiration dates for external recipients. Avoid embedding sensitive data in spreadsheet file names. Remove metadata and hidden sheets before sharing externally. Always verify recipient email addresses before sending access invitations.',
  },
  {
    icon: 'ri-alert-line',
    title: 'Threat Detection & Response',
    desc: 'Monitor for unusual access patterns such as logins from unfamiliar locations or bulk data exports at odd hours. Set up automated alerts for suspicious activities and maintain an incident response checklist that your team can follow immediately when a breach is suspected.',
  },
  {
    icon: 'ri-file-shield-2-line',
    title: 'Compliance & Regulatory Alignment',
    desc: 'Align spreadsheet practices with relevant regulations including GDPR for European customer data, CCPA for California residents, and industry-specific requirements like HIPAA for healthcare data. Document your compliance measures and review them quarterly for gaps.',
  },
];

export default function SecurityPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <article className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <nav className="mb-8">
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors flex items-center gap-1.5">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold">Security</span>
              <span className="px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-semibold">Best Practices 2026</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-900 leading-tight mb-4">
              VigorBuy Spreadsheet Security Guide: How to Protect Your Business Data
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              A complete security framework for spreadsheet users — covering access control, encryption, backup strategies, safe sharing, and regulatory compliance to ensure your sensitive business data stays protected.
            </p>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20security%20concept%20visualization%20with%20shield%20and%20lock%20abstract%20geometric%20shapes%2C%20soft%20warm%20cream%20background%20with%20gentle%20coral%20accent%20elements%2C%20clean%20organized%20layout%20suggesting%20data%20protection%2C%20natural%20diffused%20lighting%2C%20editorial%20style%20photography%2C%20subtle%20sage%20green%20accents%2C%20trustworthy%20and%20professional%20atmosphere%2C%20no%20text%2C%20no%20faces%2C%20no%20screens%20with%20visible%20content&width=1200&height=600&seq=security-hero-2026-v2&orientation=landscape"
              alt="Spreadsheet data security protection concept"
              className="w-full h-auto object-cover object-center"
            />
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Why Spreadsheet Security Cannot Be an Afterthought</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Spreadsheets are among the most widely used business tools — and among the most frequently breached. A 2025 study found that 63% of businesses experienced at least one data exposure incident involving spreadsheet files in the previous year, with the average cost of a spreadsheet-related breach exceeding $120,000 in direct losses and remediation expenses.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              The <strong>VigorBuy Spreadsheet Security Framework</strong> addresses these risks head-on. Every template and guide in our ecosystem incorporates security best practices by design — not as an optional add-on, but as a fundamental layer that protects your data without slowing down your workflow. Whether you manage customer records, financial statements, or proprietary business intelligence, the principles in this guide apply universally.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Security is not a one-time configuration — it is an ongoing practice. This guide provides both the foundational setup steps and the maintenance routines that keep your spreadsheet environment secure as your team grows and your data evolves. For an overview of all our resources, start with the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline">VigorBuy Spread homepage</Link>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">The Eight Pillars of Spreadsheet Security</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {securityPrinciples.map((pillar) => (
                <div key={pillar.title} className="group p-5 rounded-2xl bg-background-100 border border-background-200/70 hover:border-accent-300 hover:shadow-md transition-all duration-300">
                  <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mb-3 group-hover:bg-accent-100 transition-colors duration-300">
                    <i className={`${pillar.icon} text-accent-600 w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1.5">{pillar.title}</h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">{pillar.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Security Checklist for Every Spreadsheet</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Before deploying any spreadsheet into production use — or sharing it with team members — run through this checklist to ensure you have not overlooked critical security measures:
            </p>
            <div className="bg-background-100 rounded-2xl p-6 space-y-3">
              {[
                'Remove all test data, placeholder values, and debugging notes from the workbook',
                'Protect all formula cells and data validation ranges against accidental edits',
                'Verify that sensitive columns are hidden or masked for each sharing permission level',
                'Confirm automated backup is configured and the most recent backup completed successfully',
                'Review the sharing settings — are view-only links appropriate for this recipient?',
                'Set expiration dates on all externally shared links where possible',
                'Strip document metadata, author information, and revision history from external copies',
                'Document the access control list — who has what level of permission and why',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                  <p className="text-foreground-700 text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Common Security Mistakes to Avoid</h2>
            <div className="flex flex-col gap-4">
              {[
                { mistake: 'Using the same password across multiple spreadsheets', fix: 'Create unique, strong passwords for each workbook. Use a password manager to avoid memorization burden.' },
                { mistake: 'Sharing spreadsheets as email attachments without protection', fix: 'Always use cloud-based sharing with view-only links and expiration dates. Never attach sensitive spreadsheets directly to emails.' },
                { mistake: 'Leaving sensitive data in hidden sheets or columns', fix: 'Hidden is not secure. Remove or redact sensitive data before sharing. Use cell-level protection combined with actual data removal.' },
                { mistake: 'Neglecting to revoke access when team members leave', fix: 'Maintain an access control log and review it monthly. Immediately revoke permissions when roles change or employees depart.' },
                { mistake: 'Skipping backup verification', fix: 'Test backup restoration quarterly. A backup that cannot be restored is not a backup — it is false confidence.' },
                { mistake: 'Ignoring spreadsheet audit logs', fix: 'Review version history weekly for unexpected changes. Set up automated alerts for modifications to critical formula cells or data ranges.' },
              ].map((item, i) => (
                <div key={i} className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                      <i className="ri-close-line text-red-400 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{item.mistake}</h3>
                      <p className="text-foreground-600 text-xs leading-relaxed"><strong className="text-foreground-800">Fix:</strong> {item.fix}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Security in the VigorBuy Spreadsheet Ecosystem</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Every VigorBuy Spreadsheet template ships with security defaults already configured. Protected formula ranges prevent accidental corruption of critical calculations. Data validation rules block invalid entries that could cascade into reporting errors. Sharing recommendation guides help you set appropriate permissions for each recipient type — from internal team members to external auditors.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              For a broader view of how VigorBuy Spreadsheet compares to other platforms on security dimensions, see our <Link to="/vigorbuy-spreadsheet-comparison" className="text-primary-500 hover:text-primary-600 font-medium underline">platform comparison guide</Link>. If you are evaluating whether VigorBuy Spreadsheet is right for your security requirements, the <Link to="/vigorbuy-spreadsheet-review" className="text-primary-500 hover:text-primary-600 font-medium underline">in-depth review</Link> covers security scoring in detail.
            </p>
          </section>

          <section className="mb-10">
            <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-primary-50 border border-primary-200">
              <div className="flex-1">
                <h3 className="font-heading font-bold text-foreground-900 text-lg mb-1">Secure Your Spreadsheet Workflow</h3>
                <p className="text-foreground-600 text-sm">Explore templates with built-in security best practices and protect your business data.</p>
              </div>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-all duration-200 shadow-lg shadow-primary-500/20 whitespace-nowrap text-center cursor-pointer"
              >
                Visit Main Store
              </a>
            </div>
          </section>

          <div className="border-t border-background-200/70 pt-8 mt-8">
            <p className="text-foreground-500 text-xs text-center mb-4">
              This security guide is part of the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">VigorBuy Spread</Link> knowledge base. Also read: <Link to="/vigorbuy-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium">complete guide</Link> &middot; <Link to="/vigorbuy-spreadsheet-review" className="text-primary-500 hover:text-primary-600 font-medium">review</Link> &middot; <Link to="/vigorbuy-spreadsheet-templates" className="text-primary-500 hover:text-primary-600 font-medium">templates</Link>.
            </p>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}